import { config } from './config';
import { getJson } from './api';

/**
 * Fetches this tenant's entitlements once at boot — the "self-config" primitive
 * (TASK157 D3). Each client extension checks its own flag before rendering/calling,
 * so on/off + tuning live in the empavision dashboard, NOT in marketplace code.
 *
 * Goes through the host proxy `/api/empavision/capabilities`, which forwards to
 * empavision's `/api/tenant/capabilities` with the service token + integration id
 * injected server-side. Fails soft to `{}` (every extension then reads its flag as
 * disabled and renders nothing) so a missing/erroring capabilities call never
 * breaks the host.
 *
 * Expected shape (server-internal secrets already stripped upstream):
 *   { aiSearch: { enabled, similarityThreshold, maxResults }, enrichmentBadges: { enabled }, ... }
 *
 * NOTE: the host-side `/api/empavision/capabilities` proxy is the optional server
 * task (ext server 1.2) — until it's mounted this returns `{}` (fail-soft), which
 * is the correct disabled-by-default behavior.
 *
 * @param {{ rootUrl?: string }} [opts]
 * @returns {Promise<Record<string, any>>}
 */
export const fetchCapabilities = async ({ rootUrl } = {}) => {
  if (!config.isEnabled) {
    return {};
  }
  const data = await getJson('/api/empavision/capabilities', { rootUrl });
  return data || {};
};

/**
 * Convenience: read a single feature's capability block from a fetched
 * capabilities object, defaulting to `{ enabled: false }`.
 * @param {Record<string, any>} capabilities
 * @param {string} feature - e.g. 'aiSearch'
 * @returns {{ enabled: boolean } & Record<string, any>}
 */
export const featureCapability = (capabilities, feature) => {
  const block = capabilities && capabilities[feature];
  return block && typeof block === 'object' ? block : { enabled: false };
};
